import {
  platforms_shopify,
  platforms_shopify_alias,
  shopify_sync_route,
  VITE_API_URL,
} from "@/constants";
import type { AuthFetchParams } from "./global";
import type { ShopifySession } from "@/lib/types";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { isTokenInvalid } from "@/lib/api-client";
import { toast } from "sonner";
import i18n from "@/i18n";

export async function getAccounts(params: AuthFetchParams) {
  const resp = await fetch(`${VITE_API_URL}${platforms_shopify}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  return (await resp.json()) as ShopifySession[];
}

export const shopifyAccountsQueryOpts = (params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["shopifyAccounts"],
    queryFn: async () => await getAccounts(params),
  });

export async function getAccount(alias: string, params: AuthFetchParams) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_shopify_alias}/${encodeURIComponent(alias)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  if (!resp.ok) {
    throw new Error("Failed to load shopify account");
  }

  return (await resp.json()) as ShopifySession;
}

export const shopifyAccountQueryOpts = (
  alias: string,
  params: AuthFetchParams,
) =>
  queryOptions({
    queryKey: ["shopifyAccount", alias],
    queryFn: async () => await getAccount(alias, params),
  });

export const useShopifySuccessMutation = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${VITE_API_URL}${shopify_sync_route}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
      });

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const token = res.headers.get("access-token");
      if (token) setToken(token);

      if (res.status > 399) {
        if (res.status === 500) {
          throw new Error("Internal error redirecting to Shopify");
        }
      }

      return res.json();
    },
    onError: () => {
      toast.error(i18n.t("toast.subscriptionFailed"));
    },
  });
};
